import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  billingPlans, billingMe, billingWallets, billingSubmit,
  billingMyPayments, billingVerify, fmtApiError,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Copy, CheckCircle2, XCircle, Clock, RefreshCw, Sparkles } from "lucide-react";

const NETWORKS = [
  { id: "TRC20", label: "USDT · TRC-20 (Tron)",  walletKey: "trc20" },
  { id: "BEP20", label: "USDT · BEP-20 (BSC)",   walletKey: "bep20" },
];

export default function BillingPage() {
  const { subscription, refresh } = useAuth();
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ["billing-plans"], queryFn: billingPlans,
  });
  const { data: meData } = useQuery({
    queryKey: ["billing-me"], queryFn: billingMe,
  });
  const { data: walletsData } = useQuery({
    queryKey: ["billing-wallets"], queryFn: billingWallets,
  });
  const { data: paymentsData } = useQuery({
    queryKey: ["billing-payments"], queryFn: billingMyPayments,
  });

  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [network, setNetwork] = useState("TRC20");
  const [txHash, setTxHash] = useState("");

  const wallets = walletsData || { trc20: "", bep20: "" };
  const activeWallet = NETWORKS.find((n) => n.id === network)?.walletKey === "trc20" ? wallets.trc20 : wallets.bep20;
  const plans = plansData?.plans || [];

  const submitMutation = useMutation({
    mutationFn: () => billingSubmit({ plan_key: selectedPlan, network, tx_hash: txHash.trim() }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["billing-payments"] });
      qc.invalidateQueries({ queryKey: ["billing-me"] });
      refresh();
      if (data.status === "verified") {
        toast({ title: "Payment verified ✅", description: "Your plan is now active." });
        setTxHash("");
      } else {
        toast({ title: "Payment submitted", description: "We're verifying it on-chain. This usually takes 1-5 minutes." });
        setTxHash("");
      }
    },
    onError: (e) => {
      const err = e?.response?.data?.detail;
      toast({ variant: "destructive", title: "Submit failed", description: typeof err === "string" ? err : fmtApiError(err) });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (id) => billingVerify(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["billing-payments"] });
      qc.invalidateQueries({ queryKey: ["billing-me"] });
      refresh();
      if (data.status === "verified") {
        toast({ title: "Payment verified ✅" });
      } else {
        toast({ title: "Not yet confirmed", description: data.verification_details?.reason || "Try again in a minute." });
      }
    },
  });

  const copy = (v) => {
    navigator.clipboard?.writeText(v);
    toast({ title: "Copied to clipboard" });
  };

  return (
    <AppShell>
      <div className="px-6 py-8 max-w-5xl space-y-8" data-testid="billing-page">
        {/* Header */}
        <header className="space-y-2">
          <h1 className="heading text-3xl md:text-4xl font-bold tracking-tighter">
            Billing & <span className="text-gold-grad">Plans</span>
          </h1>
          <p className="text-sm text-white/60">
            All plans grant full access — signals, charts, alerts, Telegram. Pay in USDT (TRC-20 or BEP-20).
          </p>
        </header>

        {/* Status */}
        {subscription && (
          <div
            data-testid="billing-status-card"
            className="rounded-xl border border-white/10 bg-white/5 px-5 py-4 flex items-center justify-between"
          >
            <div className="space-y-1">
              <div className="text-xs uppercase tracking-widest text-white/40">Current status</div>
              <div className="text-lg font-semibold">
                {subscription.status === "active" && (subscription.plan === "admin" ? "Admin · Unlimited" :
                  `Active — ${subscription.plan?.toUpperCase()} · ${subscription.days_remaining} day${subscription.days_remaining === 1 ? "" : "s"} left`)}
                {subscription.status === "trial" && `Free trial — ${subscription.days_remaining} day${subscription.days_remaining === 1 ? "" : "s"} left`}
                {subscription.status === "expired" && <span className="text-red-300">Expired — please renew</span>}
              </div>
            </div>
            {subscription.status === "active" && !subscription.in_trial && (
              <Sparkles className="size-8 text-amber-300" />
            )}
          </div>
        )}

        {/* Plan picker */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Choose a plan</h2>
          {plansLoading ? (
            <Loader2 className="size-5 animate-spin text-white/50" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {plans.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`plan-card-${p.key}`}
                  onClick={() => setSelectedPlan(p.key)}
                  className={`relative rounded-xl border px-4 py-5 text-left transition-all ${
                    selectedPlan === p.key
                      ? "border-amber-400/60 bg-amber-400/5 ring-1 ring-amber-400/40"
                      : "border-white/10 bg-white/5 hover:border-white/20"
                  }`}
                >
                  <div className="text-xs uppercase tracking-widest text-white/50">{p.label}</div>
                  <div className="mt-2 text-3xl font-bold tabular-nums">
                    ${p.price_usdt}
                    <span className="text-sm font-normal text-white/40 ml-1">USDT</span>
                  </div>
                  <div className="mt-1 text-xs text-white/50">{p.duration_days} days</div>
                  {p.key === "yearly" && (
                    <div className="absolute top-2 right-2 text-[10px] uppercase tracking-widest bg-amber-400/20 text-amber-200 px-1.5 py-0.5 rounded">
                      Best value
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Payment */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Pay with USDT</h2>
          <div className="flex gap-2">
            {NETWORKS.map((n) => (
              <button
                key={n.id}
                type="button"
                data-testid={`network-${n.id.toLowerCase()}`}
                onClick={() => setNetwork(n.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  network === n.id ? "bg-amber-400/90 text-ink-900" : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                {n.label}
              </button>
            ))}
          </div>

          {/* Wallet address */}
          <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3">
            <div className="text-xs uppercase tracking-widest text-white/40 mb-1">Send USDT to</div>
            {activeWallet ? (
              <div className="flex items-center gap-2">
                <code data-testid="billing-wallet-address" className="text-sm font-mono break-all text-amber-200">
                  {activeWallet}
                </code>
                <button onClick={() => copy(activeWallet)} className="shrink-0 text-white/50 hover:text-white" data-testid="copy-wallet-btn">
                  <Copy className="size-4" />
                </button>
              </div>
            ) : (
              <div className="text-sm text-red-300" data-testid="billing-wallet-missing">
                ⚠ Admin has not configured a {network} wallet yet. Please ask your administrator to set one in Admin → Config.
              </div>
            )}
          </div>

          {/* Tx hash input */}
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-widest text-white/40">Transaction hash</label>
            <input
              data-testid="billing-tx-hash"
              type="text"
              value={txHash}
              onChange={(e) => setTxHash(e.target.value)}
              placeholder="0x… (after sending USDT, paste your transaction hash here)"
              className="w-full bg-ink-800 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono text-white/90 focus:outline-none focus:border-amber-400/60"
            />
            <p className="text-xs text-white/40">
              We verify on-chain automatically — usually within 1-5 minutes.
            </p>
          </div>

          <button
            data-testid="billing-submit-btn"
            onClick={() => submitMutation.mutate()}
            disabled={!txHash.trim() || !activeWallet || submitMutation.isPending}
            className="px-4 py-2 rounded-lg bg-gold-grad text-ink-900 font-semibold text-sm disabled:opacity-40 inline-flex items-center gap-2"
          >
            {submitMutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Submit payment for verification
          </button>
        </section>

        {/* My payments */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Recent payments</h2>
          <div className="rounded-xl border border-white/10 divide-y divide-white/5 overflow-hidden" data-testid="billing-payments-list">
            {(paymentsData?.payments || []).length === 0 && (
              <div className="px-4 py-6 text-sm text-white/40">No payments yet.</div>
            )}
            {(paymentsData?.payments || []).map((p) => (
              <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3 bg-white/[0.02]">
                <div className="text-sm flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-white/40">
                    <span className="font-mono">{(p.tx_hash || "").slice(0, 12)}…</span>
                    <span>·</span>
                    <span>{p.network}</span>
                    <span>·</span>
                    <span>${p.amount_usdt} {p.plan_key}</span>
                  </div>
                  <div className="text-white/80 text-xs mt-0.5">
                    {new Date(p.created_at).toLocaleString()}
                  </div>
                  {p.verification_details?.reason && (
                    <div className="text-xs text-red-300 mt-1">Reason: {p.verification_details.reason}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {p.status === "verified" && <span className="text-xs text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="size-4" /> Verified</span>}
                  {p.status === "failed"   && <span className="text-xs text-red-300 inline-flex items-center gap-1"><XCircle className="size-4" /> Failed</span>}
                  {p.status === "pending"  && (
                    <button
                      data-testid={`reverify-${p.id}`}
                      onClick={() => verifyMutation.mutate(p.id)}
                      className="text-xs text-amber-200 inline-flex items-center gap-1 hover:underline"
                    >
                      {verifyMutation.isPending ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
                      <Clock className="size-3" /> Re-verify
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </AppShell>
  );
}
